import { supabase } from '../lib/supabase';
import { getMultipleProjectTotals } from './projectCostService';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

function toSnakeCase(obj) {
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeObj[snakeKey] = obj?.[key];
  }
  return snakeObj;
}

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  }
  return camelObj;
}

export const projectService = {
  async getAll({ statuses, excludeStatuses } = {}) {
    try {
      // Default: active only (preserves all existing callers' behavior)
      const statusFilter = statuses ?? ['active'];

      // After MIG 2: superintendent_user_id renamed to supervisor_user_id.
      // The FK alias 'superintendent' is updated to 'supervisor'.
      let query = supabase?.from('projects')?.select('*, pm:user_profiles!pm_user_id(id, full_name, email, logo_url), coordinator:user_profiles!coordinator_user_id(id, full_name, email, logo_url), supervisor:user_profiles!supervisor_user_id(id, full_name, email, logo_url), assistant:user_profiles!assistant_user_id(id, full_name, email, logo_url)')?.order('created_at', { ascending: false });

      if (excludeStatuses && excludeStatuses?.length > 0) {
        // Exclusion mode: show all projects EXCEPT the listed statuses
        // Only references ENUM values that are guaranteed to exist ('archived', 'deleted')
        query = query?.not('status', 'in', `(${excludeStatuses?.join(',')})`);
      } else if (statusFilter?.length === 1) {
        query = query?.eq('status', statusFilter?.[0]);
      } else {
        query = query?.in('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.error('Error fetching projects:', error?.message, '| code:', error?.code);
          throw error;
        }
      }

      const projects = data?.map(toCamelCase) || [];

      // Get all project IDs
      const projectIds = projects?.map(p => p?.id)?.filter(Boolean);

      if (projectIds?.length > 0) {
        // Fetch aggregated totals from project_costs for all projects
        const totalsMap = await getMultipleProjectTotals(projectIds);

        // Attach calculated values to each project
        return projects?.map(project => {
          const totals = totalsMap?.[project?.id] || { totalBudget: 0, totalActual: 0, totalProfit: 0 };
          return {
            ...project,
            contractValue: totals?.totalBudget,
            totalSubRevised: totals?.totalActual,
            expenses: totals?.totalActual,
            profit: totals?.totalProfit
          };
        });
      }

      return projects;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('projects')
        // After MIG 2: superintendent_user_id renamed to supervisor_user_id
        ?.select('*, pm:user_profiles!pm_user_id(id, full_name, email, logo_url), coordinator:user_profiles!coordinator_user_id(id, full_name, email, logo_url), supervisor:user_profiles!supervisor_user_id(id, full_name, email, logo_url), assistant:user_profiles!assistant_user_id(id, full_name, email, logo_url)')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  },

  // FUTURE: This will be replaced by reading projects from OnSiteX Core DB.
  // Keep creation logic isolated in this service layer for easy migration.
  async createProject(projectData) {
    console.log("🧱 createProject received:", projectData);
    try {
      const orgId = await getCurrentOrgId();
      const insertData = {
        name: projectData?.name,
        // Pass null if empty — trigger will auto-generate the project number
        project_number: projectData?.projectNumber?.trim() || null,
        client_project_number: projectData?.clientProjectNumber?.trim() || null,
        client_name: projectData?.clientName || null,
        contract_value: projectData?.contractValue || 0,
        construction_type: projectData?.constructionType || null,
        work_nature: projectData?.workNature || null,
        architect: projectData?.architect || null,
        submission_date: projectData?.submissionDate || null,
        address: projectData?.address || null,
        city: projectData?.city || null,
        state: projectData?.state || null,
        zip_code: projectData?.zipCode || null,
        description: projectData?.description || null,
        start_date: projectData?.startDate || null,
        end_date: projectData?.endDate || null,
        status: projectData?.status || 'active',
        pm_user_id: projectData?.pmUserId || null,
        // After MIG 2: renamed from superintendent_user_id to supervisor_user_id
        supervisor_user_id: projectData?.supervisorUserId ?? projectData?.superintendentUserId ?? null,
        // coordinator_user_id: reinstated — writes to the existing DB column with legacy data
        coordinator_user_id: projectData?.coordinatorUserId || null,
        // assistant_user_id replaces coordinator_user_id in the UI (coordinator_user_id kept in DB for legacy data)
        assistant_user_id: projectData?.assistantUserId || null,
        organization_id: orgId
      };

      console.log("📝 Inserting into Supabase:", insertData);
      const { data, error } = await supabase
        ?.from('projects')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw error;
        }
      }

      console.log("🎉 Supabase returned:", data);
      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error("💥 Supabase error:", error);
      console.error('Error creating project:', error);
      throw error;
    }
  },

  async create(projectData) {
    try {
      const orgId = await getCurrentOrgId();
      const snakeData = { ...toSnakeCase(projectData), organization_id: orgId };
      const { data, error } = await supabase?.from('projects')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  async update(id, projectData) {
    try {
      const updateData = {
        name: projectData?.name,
        project_number: projectData?.projectNumber?.trim() || null,
        client_project_number: projectData?.clientProjectNumber?.trim() || null,
        client_name: projectData?.clientName || null,
        contract_value: projectData?.contractValue || 0,
        start_date: projectData?.startDate || null,
        end_date: projectData?.endDate || null,
        status: projectData?.status || 'active',
        construction_type: projectData?.constructionType || null,
        work_nature: projectData?.workNature || null,
        architect: projectData?.architect || null,
        submission_date: projectData?.submissionDate || null,
        address: projectData?.address || null,
        city: projectData?.city || null,
        state: projectData?.state || null,
        zip_code: projectData?.zipCode || null,
        description: projectData?.description || null,
        pm_user_id: projectData?.pmUserId !== undefined ? (projectData?.pmUserId || null) : undefined,
        // After MIG 2: renamed from superintendent_user_id to supervisor_user_id
        // Only include if explicitly provided in payload (not undefined)
        supervisor_user_id: projectData?.supervisorUserId !== undefined ? (projectData?.supervisorUserId || null) : undefined,
        // coordinator_user_id: reinstated — only include if explicitly provided (diff-style protection)
        coordinator_user_id: projectData?.coordinatorUserId !== undefined ? (projectData?.coordinatorUserId || null) : undefined,
        // assistant_user_id: only include if explicitly provided
        assistant_user_id: projectData?.assistantUserId !== undefined ? (projectData?.assistantUserId || null) : undefined,
        updated_at: new Date()?.toISOString()
      };

      // Remove undefined keys so Supabase doesn't overwrite with null
      Object.keys(updateData)?.forEach(key => {
        if (updateData?.[key] === undefined) delete updateData?.[key];
      });

      const { data, error } = await supabase?.from('projects')?.update(updateData)?.eq('id', id)?.select('*, pm:user_profiles!pm_user_id(id, full_name, email, logo_url), coordinator:user_profiles!coordinator_user_id(id, full_name, email, logo_url), supervisor:user_profiles!supervisor_user_id(id, full_name, email, logo_url), assistant:user_profiles!assistant_user_id(id, full_name, email, logo_url)')?.single();

      console.log('[UPDATE RESULT]', data, error);

      if (error) {
        console.error('[UPDATE ERROR]', error);
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      if (!data) return null;

      const project = toCamelCase(data);

      // Fetch aggregated totals from project_costs
      const totalsMap = await getMultipleProjectTotals([project?.id]);
      const totals = totalsMap?.[project?.id] || { totalBudget: 0, totalActual: 0, totalProfit: 0 };

      // Return project with calculated values
      return {
        ...project,
        contractValue: totals?.totalBudget,
        totalSubRevised: totals?.totalActual,
        expenses: totals?.totalActual,
        profit: totals?.totalProfit
      };
    } catch (error) {
      console.error('[UPDATE ERROR]', error);
      console.error('Error updating project:', error);
      throw error;
    }
  },

  async getVisibleProjectCount() {
    try {
      const { count, error } = await supabase
        ?.from('projects')
        ?.select('*', { count: 'exact', head: true })
        ?.not('status', 'in', '(archived,deleted)');

      if (error) {
        console.error('Error fetching project count:', error?.message);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error fetching project count:', error);
      return 0;
    }
  },

  /**
   * Fetch active projects for the Gantt schedule view.
   * Returns lightweight project data: id, project_number, name, start_date, end_date,
   * pm_user_id, pm full_name, contract_value, status — sorted by start_date ASC.
   * RLS automatically scopes results by org and role.
   */
  async getScheduleProjects() {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select('id, project_number, name, start_date, end_date, pm_user_id, contract_value, status, pm:user_profiles!pm_user_id(id, full_name)')
        ?.eq('status', 'active')
        ?.order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching schedule projects:', error?.message);
        return [];
      }

      return (data || [])?.map(p => ({
        id: p?.id,
        projectNumber: p?.project_number,
        name: p?.name,
        startDate: p?.start_date,
        endDate: p?.end_date,
        pmUserId: p?.pm_user_id,
        pmFullName: p?.pm?.full_name || null,
        contractValue: p?.contract_value || 0,
        status: p?.status
      }));
    } catch (error) {
      console.error('Error fetching schedule projects:', error);
      return [];
    }
  },

  // Lightweight status-only update — does NOT touch any team assignment fields.
  // Use this from the inline status dropdown to avoid overwriting pm/supervisor/assistant.
  async updateStatus(id, newStatus) {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.update({ status: newStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw error;
        }
      }

      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error('Error updating project status:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  async bulkDelete(ids) {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error bulk deleting projects:', error);
      throw error;
    }
  },

  async archive(id) {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.update({ status: 'archived' })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error('Error archiving project:', error);
      throw error;
    }
  },

  async softDelete(id) {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.update({ status: 'deleted' })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error('Error soft deleting project:', error);
      throw error;
    }
  }
};